import React, { useCallback } from 'react';
import { Trans } from '@wix/yoshi-flow-editor';
import { TextButton, Text } from 'wix-ui-tpa/cssVars';
import { useSettings } from '@wix/tpa-settings/react';
import type { Link } from '@wix/document-services-types';
import settingsParams from '../../settingsParams';
import { classes } from '../Widget.st.css';

interface ConsentStatementProps {
  navigateToLink: (link: Link) => void;
}

export const ConsentStatement: React.FC<ConsentStatementProps> = ({
  navigateToLink,
}) => {
  const settings = useSettings();
  const termsLink = settings.get(settingsParams.termsOfUseLink);
  const privacyLink = settings.get(settingsParams.privacyPolicyLink);

  // Handle link navigation - external links use window directly (browser context),
  // internal links use the controller's navigateToLink
  const handleLinkClick = useCallback(
    (link: Link) => {
      if (link?.type === 'ExternalLink' && 'url' in link) {
        const target = (link as any).target || '_blank';
        if (target === '_blank') {
          window.open(link.url, '_blank', 'noopener,noreferrer');
        } else {
          window.location.href = link.url;
        }
      } else {
        // Internal links - use controller's navigateToLink
        navigateToLink(link);
      }
    },
    [navigateToLink],
  );

  // Only show if at least one link exists
  if (!termsLink && !privacyLink) {
    return null;
  }

  return (
    <div data-hook="signup-consent-statement">
      <Text className={classes.consentStatementText}>
        {termsLink && privacyLink && (
          <Trans
            i18nKey="memberAuthentication.uouScreens.signUpForm.consentStatement"
            components={{
              '1': (
                <TextButton
                  as="button"
                  // Prevent focus change on pointer down so the input doesn't blur before this action
                  onPointerDown={(e) => e.preventDefault()}
                  onClick={() => handleLinkClick(termsLink)}
                  className={classes.link}
                  data-hook="signup-terms-of-use-link"
                />
              ),
              '2': (
                <TextButton
                  as="button"
                  // Prevent focus change on pointer down so the input doesn't blur before this action
                  onPointerDown={(e) => e.preventDefault()}
                  onClick={() => handleLinkClick(privacyLink)}
                  className={classes.link}
                  data-hook="signup-privacy-policy-link"
                />
              ),
            }}
          />
        )}
        {termsLink && !privacyLink && (
          <Trans
            i18nKey="memberAuthentication.uouScreens.signUpForm.termsOfUse"
            components={{
              '1': (
                <TextButton
                  as="button"
                  // Prevent focus change on pointer down so the input doesn't blur before this action
                  onPointerDown={(e) => e.preventDefault()}
                  onClick={() => handleLinkClick(termsLink)}
                  className={classes.link}
                  data-hook="signup-terms-of-use-link"
                />
              ),
            }}
          />
        )}
        {!termsLink && privacyLink && (
          <Trans
            i18nKey="memberAuthentication.uouScreens.signUpForm.privacyPolicy"
            components={{
              '1': (
                <TextButton
                  as="button"
                  // Prevent focus change on pointer down so the input doesn't blur before this action
                  onPointerDown={(e) => e.preventDefault()}
                  onClick={() => handleLinkClick(privacyLink)}
                  className={classes.link}
                  data-hook="signup-privacy-policy-link"
                />
              ),
            }}
          />
        )}
      </Text>
    </div>
  );
};
